import React, { MouseEventHandler, useState } from 'react';

import { MenuItem } from '@mui/material';
import { NestedMenuItem } from 'mui-nested-menu';

import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { useConfigurationStore } from 'store/configurationsStore';

import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';

import {
    PinboardWidgetWrapperContextType,
    usePinboardWidgetWrapperContext,
} from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';
import { WidgetSizes } from 'components/Dashboards/Reporting/reporting.types';
import classes from 'components/Dashboards/Reporting/widgetInReport/components/WidgetHeader/SizesMenu.module.scss';

type Dir = 'left' | 'right';

type sizesToLabels = Record<WidgetSizes, string>;
const sizesLabels: sizesToLabels = {
    ['small']: 'Small',
    ['smallLong']: 'Small-Long',
    ['mediumShort']: 'Medium-Short',
    ['medium']: 'Medium',
    ['largeShort']: 'Large-Short',
    ['large']: 'Large',
    ['extraLarge']: 'Extra Large',
};

const menuPropsFor = (dir: Dir) =>
    dir === 'left'
        ? {
              anchorOrigin: { vertical: 'top', horizontal: 'left' as const },
              transformOrigin: { vertical: 'top', horizontal: 'right' as const },
              PaperProps: {
                  className: classes.menuPaper,
              },
          }
        : {
              anchorOrigin: { vertical: 'top', horizontal: 'right' as const },
              transformOrigin: { vertical: 'top', horizontal: 'left' as const },
              PaperProps: {
                  className: classes.menuPaper,
              },
          };

const estimatedSubItemWidth: number = 85;
const estimatedSideMenuWidth: number = 270;

interface Props {
    changeWidgetSize: PinboardWidgetWrapperContextType['onWidgetChangeSize'];
    widgetComponentKey: string;
    widgetId: number | string | undefined;
}

export const WidgetSizesMenu = ({ changeWidgetSize, widgetComponentKey, widgetId }: Props) => {
    const configurations = useConfigurationStore();
    const [dir, setDir] = useState<Dir>('left');
    // const sizes = getSizesByComponentKey(widgetComponentKey);
    const sizes: WidgetSizes[] = Object.values(configurations[widgetComponentKey]?.availableSizes) ?? [];
    const { selectedWidgetSize } = usePinboardWidgetWrapperContext();
    const { sendEventWithDimensions } = useEventWithDimensions();

    const handleOnMouseEnter: MouseEventHandler<HTMLElement> = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const spaceToTheRight = window.innerWidth - estimatedSideMenuWidth - rect.left;
        if (spaceToTheRight > estimatedSubItemWidth) {
            setDir('right');
        } else {
            setDir('left');
        }
    };

    const handleSizeChange = (widgetId: string, size: WidgetSizes, componentKey: string): void => {
        const componentName = configurations[componentKey]?.name || 'Unknown';
        const sizeLabel = translateValueToLabel(size);

        sendEventWithDimensions({
            category: 'Reporting',
            action: 'Component Resized',
            label: `${componentName} - ${sizeLabel}`,
        });

        changeWidgetSize?.(widgetId, size);
    };

    return widgetId && changeWidgetSize ? (
        <div className={classes.nestedMenuWrapper}>
            <NestedMenuItem
                onMouseEnter={handleOnMouseEnter}
                parentMenuOpen={true}
                label={'Change size'}
                leftIcon={<MuiIcon icon={'aspect_ratio_outlined'} fontSize={'16px'} />}
                rightIcon={<MuiIcon icon={'chevron_right'} />}
                sx={{
                    '& .MuiTypography-root': {
                        fontSize: '13px',
                        position: 'relative',
                        width: '100%',
                    },
                    '& .MuiBox-root': {
                        alignItems: 'center',
                    },
                }}
                MenuProps={menuPropsFor(dir)}
            >
                {sizes.map((size) => {
                    const isCurrentSizeChosen = selectedWidgetSize === size;
                    const className = isCurrentSizeChosen
                        ? `${classes.labelWrapper} ${classes.chosenSize}`
                        : classes.labelWrapper;
                    return (
                        <div className={className} key={size}>
                            <MenuItem
                                key={`${widgetId}_${size}`}
                                disabled={size === 'custom'}
                                onClick={() => handleSizeChange(widgetId?.toString(), size, widgetComponentKey)}
                                className={classes.menuItem}
                            >
                                <span className={classes.menuItemText}>{translateValueToLabel(size)}</span>
                                <div>
                                    {isCurrentSizeChosen && (
                                        <MuiIcon color={`var(--text-quinary)`} icon={'check'} fontSize={'16px'} />
                                    )}
                                </div>
                            </MenuItem>
                        </div>
                    );
                })}
            </NestedMenuItem>
        </div>
    ) : null;
};

const translateValueToLabel = (value: WidgetSizes): string => {
    return sizesLabels[value] ?? 'Custom';
};
